<template>
  <div class="flex bg-gray-50" :style="{ minHeight: `calc(100vh - ${topNavHeight}px)` }">
    <!-- Main Content Area - Full Width -->
    <div class="flex-1 min-w-0">
      <!-- Sticky Header Section with Filters -->
      <div ref="stickyHeader" class="sticky bg-white border-b border-gray-300 px-1 md:px-6 py-2 md:py-3 transition-all duration-200 ease-out z-50" :style="{ top: (topNavHeight - 1) + 'px' }">

        <!-- Single Row Layout -->
        <div class="flex flex-col md:flex-row items-center justify-between gap-2 md:gap-4 z-50">
          <!-- Left Side - Stats and Clear -->
          <div class="flex flex-wrap items-center gap-2 md:gap-4 hidden md:block">
            <div class="inline-flex items-center bg-indigo-100 rounded-lg px-3 py-2">
              <svg class="w-4 h-4 mr-2 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"></path>
              </svg>
              <span class="text-indigo-900 md:font-medium font-bold text-xs md:text-base">
                Explore {{ (showMap ? filteredShowsByMapBounds : filteredUpcomingShows).length }} {{ getPageTitle() }}
              </span>
            </div>
          </div>
          <!-- Right Side - Filter Buttons -->
          <div class="flex gap-1 md:gap-3 items-center md:justify-end overflow-x-auto md:overflow-x-visible overflow-y-visible max-w-full scrollbar-hide z-50" style="scrollbar-width: none; -ms-overflow-style: none;">
            <!-- Location Filter Button -->
            <div class="relative flex-shrink-0">
              <button
                @click="toggleLocationDropdown"
                class="flex items-center gap-2 px-2.5 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                :class="{ 'bg-blue-50 border-blue-300 text-blue-700': locationFilter }"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                <span class="text-sm font-medium">
                  {{ getLocationFilterLabel }}
                </span>
                <svg v-if="locationFilter" @click.stop="clearLocation" class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>

              <!-- Location Dropdown -->
              <Portal v-if="showLocationDropdown" target="#portal-target" :disabled="!isMobile">
                <div class="fixed md:absolute inset-x-0 md:inset-x-auto md:top-full left-0 md:left-0 right-0 md:right-auto mt-2 bg-white border border-gray-200 rounded-xl md:rounded-xl shadow-lg z-50 mx-2 md:mx-0 md:w-96" :style="isMobile ? { top: (headerHeight + 8) + 'px' } : {}">
                  <div class="p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Select Location</h3>
                    <div class="relative mb-4">
                      <input
                        ref="locationSearchInput"
                        v-model="locationSearchQuery"
                        @input="onLocationSearch"
                        @focus="handleLocationInputFocus"
                        @blur="handleLocationInputBlur"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-base placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Search destinations"
                        style="font-size: 16px;"
                        autocomplete="off"
                        autocorrect="off"
                        autocapitalize="off"
                        spellcheck="false"
                      />
                    </div>

                    <!-- Near Me Option -->
                    <div @click="handleNearMe" class="near-me-option flex items-center p-3 hover:bg-gray-50 cursor-pointer rounded-lg mb-2" :class="{ 'opacity-50 cursor-not-allowed': isLoadingNearMe }" :disabled="isLoadingNearMe">
                      <div class="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg v-if="!isLoadingNearMe" class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        <!-- Loading spinner -->
                        <svg v-else class="w-4 h-4 text-blue-600 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" class="opacity-25"></circle>
                          <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" class="opacity-75"></path>
                        </svg>
                      </div>
                      <div>
                        <div class="font-medium text-gray-900 text-sm">{{ isLoadingNearMe ? 'Getting location...' : 'Near me' }}</div>
                        <div class="text-xs text-gray-500">{{ isLoadingNearMe ? 'Please wait' : 'Use my current location' }}</div>
                      </div>
                    </div>

                    <!-- Popular Destinations or Search Results -->
                    <div class="max-h-48 overflow-y-auto">
                      <div v-if="locationSearchResults.length > 0">
                        <div class="text-xs font-medium text-gray-500 mb-2 px-1">Search results</div>
                        <div v-for="result in locationSearchResults" :key="result.id"
                             @click="selectSearchResult(result)"
                             class="flex items-center p-3 hover:bg-gray-50 cursor-pointer rounded-lg">
                          <div :class="[result.bgClass, 'w-8 h-8 rounded-lg flex items-center justify-center mr-3']">
                            <svg :class="result.iconClass" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                            </svg>
                          </div>
                          <div>
                            <div class="font-medium text-gray-900 text-sm">{{ result.name }}</div>
                            <div class="text-xs text-gray-500">{{ result.description }}</div>
                          </div>
                        </div>
                      </div>
                      <div v-else-if="locationSearchQuery && locationSearchQuery.length >= 2" class="p-4 text-center text-gray-500">
                        <div class="text-sm">No locations found for "{{ locationSearchQuery }}"</div>
                      </div>
                    </div>
                  </div>
                </div>
              </Portal>
            </div>

            <!-- Radius Filter Button - Only show when location is selected -->
            <div v-if="selectedLocation && !isStateSelected" class="relative flex-shrink-0">
              <button
                @click="toggleRadiusDropdown"
                class="flex items-center gap-2 px-2.5 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                :class="{ 'bg-blue-50 border-blue-300 text-blue-700': selectedRadius }"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"></path>
                </svg>
                <span class="text-sm font-medium">
                  {{ getRadiusLabel() }}
                </span>
              </button>

              <!-- Radius Dropdown -->
              <Portal v-if="showRadiusDropdown" target="#portal-target" :disabled="!isMobile">
                <div class="fixed md:absolute inset-x-0 md:inset-x-auto md:top-full left-0 md:left-auto right-0 md:right-0 mt-2 bg-white border border-gray-200 rounded-xl md:rounded-xl shadow-lg z-50 mx-2 md:mx-0 md:w-48" :style="isMobile ? { top: (headerHeight + 8) + 'px' } : {}">
                  <div class="p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Search Radius</h3>
                    <div class="space-y-2">
                      <label v-for="option in radiusOptions" :key="option.value" class="flex items-center">
                        <input
                          type="radio"
                          :value="option.value"
                          v-model="selectedRadius"
                          @change="applyFilters"
                          class="border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                        />
                        <span class="text-sm text-gray-700">{{ option.label }}</span>
                      </label>
                    </div>
                  </div>
                </div>
              </Portal>
            </div>

            <!-- Species Filter Button -->
            <div class="relative flex-shrink-0">
              <button
                @click="toggleSpeciesDropdown"
                class="flex items-center gap-2 px-2.5 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors w-40"
                :class="{ 'bg-blue-50 border-blue-300 text-blue-700': speciesFilter.length > 0 }"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
                <span class="text-sm font-medium">
                  {{ speciesFilter.length > 0 ? `Species (${speciesFilter.length})` : 'Species' }}
                </span>
                <svg v-if="speciesFilter.length > 0" @click.stop="clearSpecies" class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>

              <!-- Species Dropdown -->
              <Portal v-if="showSpeciesDropdown" target="#portal-target" :disabled="!isMobile">
                <div class="fixed md:absolute inset-x-0 md:inset-x-auto md:top-full left-0 md:left-auto right-0 md:right-0 mt-2 bg-white border border-gray-200 rounded-xl md:rounded-xl shadow-lg z-50 mx-2 md:mx-0 md:w-56" :style="isMobile ? { top: (headerHeight + 8) + 'px' } : {}">
                  <div class="p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Select Species</h3>
                    <div class="space-y-2 max-h-64 overflow-y-auto">
                      <label v-for="species in speciesList" :key="species.value" class="flex items-center">
                        <input
                          type="checkbox"
                          :value="species"
                          v-model="speciesFilter"
                          @change="applyFilters"
                          class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-3"
                        />
                        <span class="text-sm text-gray-700">{{ species.label }}</span>
                      </label>
                    </div>
                    <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                      <button @click="clearSpecies" class="text-sm text-gray-500 hover:text-gray-700">Clear</button>
                      <button @click="applySpeciesFilter" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Apply</button>
                    </div>
                  </div>
                </div>
              </Portal>
            </div>

            <!-- Date Range Filter Button -->
            <div class="relative flex-shrink-0">
              <button
                @click="toggleDateDropdown"
                class="flex items-center gap-2 px-2.5 md:px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                :class="{ 'bg-blue-50 border-blue-300 text-blue-700': startDateFilter || endDateFilter }"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"></path>
                </svg>
                <span class="text-sm font-medium">
                  {{ getDateRangeLabel() }}
                </span>
                <svg v-if="startDateFilter || endDateFilter" @click.stop="clearDateRange" class="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>

              <!-- Date Range Dropdown -->
              <Portal v-if="showDateDropdown" target="#portal-target" :disabled="!isMobile">
                <div class="fixed md:absolute inset-x-0 md:inset-x-auto md:top-full left-0 md:left-auto right-0 md:right-0 mt-2 bg-white border border-gray-200 rounded-xl md:rounded-xl shadow-lg z-50 mx-2 md:mx-0 md:w-56" :style="isMobile ? { top: (headerHeight + 8) + 'px' } : {}">
                  <div class="p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Select Date Range</h3>
                    <div class="space-y-3">
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">Start Date</label>
                        <input
                          type="date"
                          v-model="tempStartDate"
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mb-3"
                          style="margin-bottom: 12px; -webkit-appearance: none;"
                        />
                      </div>
                      <div>
                        <label class="block text-xs font-medium text-gray-700 mb-1">End Date</label>
                        <input
                          type="date"
                          v-model="tempEndDate"
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          style="-webkit-appearance: none;"
                        />
                      </div>
                    </div>
                    <div class="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                      <button @click="clearDateRange" class="text-sm text-gray-500 hover:text-gray-700">Clear dates</button>
                      <button @click="applyDateFilter" class="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Apply</button>
                    </div>
                  </div>
                </div>
              </Portal>
            </div>
            <!-- Clear All Button -->
            <button
              v-if="hasActiveFilters"
              @click="clearAllFilters"
              class="text-sm text-gray-600 hover:text-gray-800 underline flex-shrink-0"
            >
              Clear all
            </button>
          </div>
        </div>

        <!-- Global Backdrop for all dropdowns -->
        <div
          v-if="showSpeciesDropdown || showLocationDropdown || showDateDropdown || showRadiusDropdown"
          @click="closeAllDropdowns"
          class="fixed inset-0 z-40"
        ></div>
      </div>

      <!-- Scrollable Content Section -->
      <div class="">
        <!-- Main Content Area -->
        <div class="flex gap-8">
          <!-- Shows List - Always Present -->
          <div class="flex-1 flex flex-col min-w-0 py-4 px-4 md:pl-8 md:pr-0 shows-list-container" :style="{ minHeight: `calc(100vh - ${headerHeight}px)` }">
            <!-- Shows Grid -->
            <div class="inline-flex items-center bg-indigo-100 rounded-lg px-3 py-2 mb-4 md:hidden">
              <svg class="w-4 h-4 mr-2 text-blue-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2z"></path>
              </svg>
              <span class="text-indigo-900 md:font-medium font-bold text-sm md:text-lg">
                Explore {{ (showMap ? filteredShowsByMapBounds : filteredUpcomingShows).length }} {{ getPageTitle() }}
              </span>
            </div>
            <div v-if="isLoadingMapBounds" class="grid gap-4 pr-2 transition-all duration-300 ease-in-out h-screen" style="grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));">
              <!-- Skeleton Loading Cards -->
              <div v-for="n in 6" :key="'skeleton-' + n" class="border border-gray-200 rounded-xl bg-white p-4 animate-pulse">
                <div class="flex space-x-4">
                  <div class="rounded-full bg-gray-200 h-12 w-12"></div>
                  <div class="flex-1 space-y-2 py-1">
                    <div class="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
                <div class="mt-4 space-y-2">
                  <div class="h-3 bg-gray-200 rounded"></div>
                  <div class="h-3 bg-gray-200 rounded w-5/6"></div>
                </div>
                <div class="mt-4 flex justify-between">
                  <div class="h-3 bg-gray-200 rounded w-1/4"></div>
                  <div class="h-3 bg-gray-200 rounded w-1/4"></div>
                </div>
              </div>
            </div>
            <div v-else-if="(showMap ? filteredShowsByMapBounds : filteredUpcomingShows).length > 0" class="min-h-screen">
              <!-- Desktop View: Show EventCard Grid -->
              <div class="">
                <div class="grid gap-4 md:pr-2 transition-all duration-300 ease-in-out" style="grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));">
                  <EventCard
                    v-for="show in (showMap ? filteredShowsByMapBounds : filteredUpcomingShows)"
                    :key="show.id"
                    :event="show"
                    :class="showMap ? '' : 'border border-gray-200 rounded-xl bg-white transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl'"
                  />
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-else class="flex-1 flex items-center justify-center min-h-screen">
              <div class="text-center max-w-md mx-auto">
                <svg class="w-20 h-20 mx-auto mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h3z"></path>
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 17l-4-4-4 4m8-8l-4-4-4 4"></path>
                </svg>
                <h3 class="text-xl font-medium text-gray-700 mb-3">No Shows Found</h3>
                <p class="text-gray-500 mb-6 leading-relaxed">
                  {{ showMap ? 'No shows are visible in the current map area. Try zooming out or adjusting your search filters.' : 'No upcoming shows match your current search criteria.' }}
                </p>
                <div class="space-y-3">
                  <p class="text-sm text-gray-400">Try:</p>
                  <div class="space-y-2 text-sm text-gray-500">
                    <div class="flex items-center justify-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                      </svg>
                      Searching a different location
      </div>
                    <div class="flex items-center justify-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"></path>
                      </svg>
                      Adjusting your species filters
    </div>
                    <div class="flex items-center justify-center">
                      <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4h3a2 2 0 012 2v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h3z"></path>
                      </svg>
                      Expanding your date range
          </div>
          </div>
        </div>
        </div>
      </div>
          </div>

          <!-- Map - Conditionally Shown -->
          <div v-show="showMap" class="hidden md:block w-1/2 lg:w-1/2 xl:w-2/5 flex-shrink-0 transition-all duration-300 ease-in-out">
            <div class="sticky overflow-hidden" :style="mapContainerStyle">
              <ShowMap :shows="filteredUpcomingShows" @clear-location="handleClearLocationFromMap" @bounds-changed="handleMapBoundsChanged" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { parse } from 'date-fns'
import EventCard from '@/components/EventCard.vue'
import ShowMap from '@/components/ShowMap.vue'
import Portal from '@/components/Portal.vue'

export default {
  components: {
    EventCard,
    ShowMap,
    Portal
  },
  data () {
    return {
      btrjla_shows: false,
      headerHeight: 70, // Default fallback, will be calculated dynamically
      topNavHeight: 70, // Initial estimated height to prevent shift, will be updated with actual height
      headerCalculationTimeout: null, // Track header calculation timeouts
      isLoadingMapBounds: false, // Add loading state for map bounds changes
      isLoadingNearMe: false, // Add loading state for Near Me functionality
      // Filter data moved from ShowSearch component
      speciesFilter: [],
      locationFilter: '',
      selectedLocation: null,
      autocomplete: null,
      startDateFilter: '',
      endDateFilter: '',
      // State radius mapping for comprehensive coverage
      stateRadiusMap: {
        'Alaska': 500,
        'Texas': 400,
        'California': 350,
        'Montana': 350,
        'New Mexico': 300,
        'Arizona': 300,
        'Nevada': 300,
        'Colorado': 300,
        'Wyoming': 300,
        'Oregon': 280,
        'Idaho': 250,
        'Utah': 250,
        'Kansas': 250,
        'Minnesota': 250,
        'Nebraska': 250,
        'South Dakota': 250,
        'North Dakota': 250,
        'Missouri': 250,
        'Oklahoma': 250,
        'Washington': 250,
        'Georgia': 230,
        'Michigan': 230,
        'Iowa': 220,
        'Illinois': 220,
        'Wisconsin': 220,
        'Florida': 220,
        'Arkansas': 210,
        'Alabama': 200,
        'North Carolina': 200,
        'New York': 200,
        'Mississippi': 200,
        'Pennsylvania': 200,
        'Louisiana': 200,
        'Tennessee': 200,
        'Ohio': 180,
        'Kentucky': 180,
        'Virginia': 180,
        'Indiana': 170,
        'Maine': 170,
        'South Carolina': 170,
        'West Virginia': 150,
        'Maryland': 120,
        'Vermont': 120,
        'New Hampshire': 120,
        'Massachusetts': 120,
        'New Jersey': 120,
        'Hawaii': 150,
        'Connecticut': 100,
        'Delaware': 80,
        'Rhode Island': 60
      },
      speciesList: [
        {
          value: 'CATTLE',
          label: 'Beef Cattle'
        },
        {
          value: 'DAIRY CATTLE',
          label: 'Dairy Cattle'
        },
        {
          value: 'SWINE',
          label: 'Swine'
        },
        {
          value: 'SHEEP',
          label: 'Sheep'
        },
        {
          value: 'GOATS',
          label: 'Meat Goats'
        },
        {
          value: 'DAIRY GOATS',
          label: 'Dairy Goats'
        },
        {
          value: 'RABBITS',
          label: 'Rabbits'
        },
        {
          value: 'POULTRY',
          label: 'Poultry'
        },
        {
          value: 'OTHER',
          label: 'Other'
        }
      ],
      showLocationDropdown: false,
      suggestedDestinations: [
        {
          id: 'northeast',
          name: 'North East',
          description: 'Northeast region livestock shows',
          type: 'region',
          region_info: {
            name: 'North East',
            coordinates: {lat: 42.3601, lng: -75.0000} // Central NY/PA area
          },
          bgClass: 'bg-blue-100',
          icon: 'RegionIcon',
          iconClass: 'text-blue-600'
        },
        {
          id: 'southeast',
          name: 'South East',
          description: 'Southeast region livestock shows',
          type: 'region',
          region_info: {
            name: 'South East',
            coordinates: {lat: 33.5000, lng: -84.0000} // Central GA/AL area
          },
          bgClass: 'bg-orange-100',
          icon: 'RegionIcon',
          iconClass: 'text-orange-600'
        },
        {
          id: 'westcoast',
          name: 'West Coast',
          description: 'West Coast region livestock shows',
          type: 'region',
          region_info: {
            name: 'West Coast',
            coordinates: {lat: 37.5000, lng: -121.0000} // Central CA area
          },
          bgClass: 'bg-green-100',
          icon: 'RegionIcon',
          iconClass: 'text-green-600'
        },
        {
          id: 'midwest',
          name: 'Mid-west',
          description: 'Midwest region livestock shows',
          type: 'region',
          region_info: {
            name: 'Mid-west',
            coordinates: {lat: 41.5000, lng: -93.5000} // Central IA/IL area
          },
          bgClass: 'bg-purple-100',
          icon: 'RegionIcon',
          iconClass: 'text-purple-600'
        }
      ],
      locationSearchResults: [],
      searchTimeout: null,
      showSpeciesDropdown: false,
      showDateDropdown: false,
      showRadiusDropdown: false, // New state for radius dropdown
      locationSearchQuery: '',
      tempStartDate: '',
      tempEndDate: '',
      selectedRadius: 50, // Default radius in miles
      radiusOptions: [
        {value: 25, label: '25 miles'},
        {value: 50, label: '50 miles'},
        {value: 100, label: '100 miles'},
        {value: 200, label: '200 miles'},
        {value: 500, label: '500 miles'}
      ],
      // Google Places session token for cost optimization
      currentSessionToken: null,
      sessionTokenTimeout: null,
      // US States database for cost optimization - avoid Places API for state searches
      usStates: {
        'alabama': { name: 'Alabama', abbr: 'AL', coords: {lat: 32.361538, lng: -86.279118} },
        'alaska': { name: 'Alaska', abbr: 'AK', coords: {lat: 61.218887, lng: -149.900009} },
        'arizona': { name: 'Arizona', abbr: 'AZ', coords: {lat: 33.729759, lng: -111.431221} },
        'arkansas': { name: 'Arkansas', abbr: 'AR', coords: {lat: 34.969704, lng: -92.373123} },
        'california': { name: 'California', abbr: 'CA', coords: {lat: 36.116203, lng: -119.681564} },
        'colorado': { name: 'Colorado', abbr: 'CO', coords: {lat: 39.059811, lng: -105.311104} },
        'connecticut': { name: 'Connecticut', abbr: 'CT', coords: {lat: 41.597782, lng: -72.755371} },
        'delaware': { name: 'Delaware', abbr: 'DE', coords: {lat: 39.318523, lng: -75.507141} },
        'florida': { name: 'Florida', abbr: 'FL', coords: {lat: 27.766279, lng: -81.686783} },
        'georgia': { name: 'Georgia', abbr: 'GA', coords: {lat: 33.040619, lng: -83.643074} },
        'hawaii': { name: 'Hawaii', abbr: 'HI', coords: {lat: 21.094318, lng: -157.498337} },
        'idaho': { name: 'Idaho', abbr: 'ID', coords: {lat: 44.240459, lng: -114.478828} },
        'illinois': { name: 'Illinois', abbr: 'IL', coords: {lat: 40.349457, lng: -88.986137} },
        'indiana': { name: 'Indiana', abbr: 'IN', coords: {lat: 39.849426, lng: -86.258278} },
        'iowa': { name: 'Iowa', abbr: 'IA', coords: {lat: 42.011539, lng: -93.210526} },
        'kansas': { name: 'Kansas', abbr: 'KS', coords: {lat: 38.5266, lng: -96.726486} },
        'kentucky': { name: 'Kentucky', abbr: 'KY', coords: {lat: 37.668140, lng: -84.670067} },
        'louisiana': { name: 'Louisiana', abbr: 'LA', coords: {lat: 31.169546, lng: -91.867805} },
        'maine': { name: 'Maine', abbr: 'ME', coords: {lat: 44.323535, lng: -69.765261} },
        'maryland': { name: 'Maryland', abbr: 'MD', coords: {lat: 39.063946, lng: -76.802101} },
        'massachusetts': { name: 'Massachusetts', abbr: 'MA', coords: {lat: 42.230171, lng: -71.530106} },
        'michigan': { name: 'Michigan', abbr: 'MI', coords: {lat: 43.326618, lng: -84.536095} },
        'minnesota': { name: 'Minnesota', abbr: 'MN', coords: {lat: 45.694454, lng: -93.900192} },
        'mississippi': { name: 'Mississippi', abbr: 'MS', coords: {lat: 32.741646, lng: -89.678696} },
        'missouri': { name: 'Missouri', abbr: 'MO', coords: {lat: 38.456085, lng: -92.288368} },
        'montana': { name: 'Montana', abbr: 'MT', coords: {lat: 47.052952, lng: -109.633040} },
        'nebraska': { name: 'Nebraska', abbr: 'NE', coords: {lat: 41.125370, lng: -98.268082} },
        'nevada': { name: 'Nevada', abbr: 'NV', coords: {lat: 38.313515, lng: -117.055374} },
        'newhampshire': { name: 'New Hampshire', abbr: 'NH', coords: {lat: 43.452492, lng: -71.563896} },
        'newjersey': { name: 'New Jersey', abbr: 'NJ', coords: {lat: 40.298904, lng: -74.521011} },
        'newmexico': { name: 'New Mexico', abbr: 'NM', coords: {lat: 34.840515, lng: -106.248482} },
        'newyork': { name: 'New York', abbr: 'NY', coords: {lat: 42.165726, lng: -74.948051} },
        'northcarolina': { name: 'North Carolina', abbr: 'NC', coords: {lat: 35.630066, lng: -79.806419} },
        'northdakota': { name: 'North Dakota', abbr: 'ND', coords: {lat: 47.528912, lng: -99.784012} },
        'ohio': { name: 'Ohio', abbr: 'OH', coords: {lat: 40.388783, lng: -82.764915} },
        'oklahoma': { name: 'Oklahoma', abbr: 'OK', coords: {lat: 35.565342, lng: -96.928917} },
        'oregon': { name: 'Oregon', abbr: 'OR', coords: {lat: 44.572021, lng: -122.070938} },
        'pennsylvania': { name: 'Pennsylvania', abbr: 'PA', coords: {lat: 40.590752, lng: -77.209755} },
        'rhodeisland': { name: 'Rhode Island', abbr: 'RI', coords: {lat: 41.680893, lng: -71.51178} },
        'southcarolina': { name: 'South Carolina', abbr: 'SC', coords: {lat: 33.856892, lng: -80.945007} },
        'southdakota': { name: 'South Dakota', abbr: 'SD', coords: {lat: 44.299782, lng: -99.438828} },
        'tennessee': { name: 'Tennessee', abbr: 'TN', coords: {lat: 35.747845, lng: -86.692345} },
        'texas': { name: 'Texas', abbr: 'TX', coords: {lat: 31.054487, lng: -97.563461} },
        'utah': { name: 'Utah', abbr: 'UT', coords: {lat: 40.150032, lng: -111.862434} },
        'vermont': { name: 'Vermont', abbr: 'VT', coords: {lat: 44.045876, lng: -72.710686} },
        'virginia': { name: 'Virginia', abbr: 'VA', coords: {lat: 37.769337, lng: -78.169968} },
        'washington': { name: 'Washington', abbr: 'WA', coords: {lat: 47.400902, lng: -121.490494} },
        'westvirginia': { name: 'West Virginia', abbr: 'WV', coords: {lat: 38.491226, lng: -80.954456} },
        'wisconsin': { name: 'Wisconsin', abbr: 'WI', coords: {lat: 44.268543, lng: -89.616508} },
        'wyoming': { name: 'Wyoming', abbr: 'WY', coords: {lat: 42.755966, lng: -107.302490} },
        'districtofcolumbia': { name: 'District of Columbia', abbr: 'DC', coords: {lat: 38.897438, lng: -77.026817} }
      },
      // State abbreviation to key mapping for quick lookup
      stateAbbrMap: {
        'AL': 'alabama', 'AK': 'alaska', 'AZ': 'arizona', 'AR': 'arkansas', 'CA': 'california',
        'CO': 'colorado', 'CT': 'connecticut', 'DE': 'delaware', 'FL': 'florida', 'GA': 'georgia',
        'HI': 'hawaii', 'ID': 'idaho', 'IL': 'illinois', 'IN': 'indiana', 'IA': 'iowa',
        'KS': 'kansas', 'KY': 'kentucky', 'LA': 'louisiana', 'ME': 'maine', 'MD': 'maryland',
        'MA': 'massachusetts', 'MI': 'michigan', 'MN': 'minnesota', 'MS': 'mississippi', 'MO': 'missouri',
        'MT': 'montana', 'NE': 'nebraska', 'NV': 'nevada', 'NH': 'newhampshire', 'NJ': 'newjersey',
        'NM': 'newmexico', 'NY': 'newyork', 'NC': 'northcarolina', 'ND': 'northdakota', 'OH': 'ohio',
        'OK': 'oklahoma', 'OR': 'oregon', 'PA': 'pennsylvania', 'RI': 'rhodeisland', 'SC': 'southcarolina',
        'SD': 'southdakota', 'TN': 'tennessee', 'TX': 'texas', 'UT': 'utah', 'VT': 'vermont',
        'VA': 'virginia', 'WA': 'washington', 'WV': 'westvirginia', 'WI': 'wisconsin', 'WY': 'wyoming',
        'DC': 'districtofcolumbia'
      }
    }
  },
  name: 'home',
  computed: {
    filteredRecentShows () {
      return this.$store.getters.filteredRecentShows
    },
    filteredUpcomingShows () {
      return this.$store.getters.filteredUpcomingShows
    },
    allUpcomingEvents () {
      return this.$store.state.upcomingEvents
    },
    searchResults () {
      return this.$store.getters.searchResults
    },
    filteredShowsByMapBounds () {
      return this.$store.getters.filteredShowsByMapBounds
    },
    showMap() {
      return this.$store.state.showMap
    },
    hasActiveFilters () {
      return this.speciesFilter.length > 0 ||
             this.locationFilter ||
             this.startDateFilter ||
             this.endDateFilter
    },
    mapContainerStyle () {
      return {
        top: `${this.headerHeight}px`,
        height: `calc(100vh - ${this.headerHeight}px)`
      }
    },
    isMobile () {
      return window.innerWidth < 768
    },
    getLocationFilterLabel() {
      if (this.locationFilter) {
        const location = this.locationFilter.length > 20 ?
          `${this.locationFilter.substring(0, 20)}...` :
          this.locationFilter

        // Don't show radius for states or regions
        if (this.selectedLocation && (this.selectedLocation.type === 'state' || this.selectedLocation.type === 'region')) {
          return location
        }

        // Get the radius label for non-state/non-region locations
        const radiusOption = this.radiusOptions.find(option => option.value === this.selectedRadius)
        const radiusLabel = radiusOption ? radiusOption.label : `${this.selectedRadius} miles`

        return `${location} (${radiusLabel})`
      } else {
        return 'Location'
      }
    },
    isStateSelected() {
      return this.selectedLocation && (this.selectedLocation.type === 'state' || this.selectedLocation.type === 'region')
    }
  },
  async mounted() {
    this.initializeFiltersFromStore()
    this.calculateHeaderHeights()
    // Remove Google Places autocomplete initialization - using custom dropdown instead
    this.handleResize() // Initial call to set proper showMap state

    // Recalculate on window resize
    window.addEventListener('resize', this.debouncedCalculateHeaderHeights)
    window.addEventListener('resize', this.handleResize)
    
    // Also listen for orientation changes on mobile devices
    window.addEventListener('orientationchange', () => {
      // Delay to allow the orientation change to complete
      setTimeout(() => {
        this.debouncedCalculateHeaderHeights()
        this.handleResize()
      }, 300)
    })
    
    // Listen for mobile menu toggle to close dropdowns
    this.$root.$on('close-filter-dropdowns', this.closeAllDropdowns)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.debouncedCalculateHeaderHeights)
    window.removeEventListener('resize', this.handleResize)
    
    // Clean up orientation change listener
    window.removeEventListener('orientationchange', this.debouncedCalculateHeaderHeights)
    
    // Clean up global event listener
    this.$root.$off('close-filter-dropdowns', this.closeAllDropdowns)
    
    // Clear any pending header calculation timeout
    if (this.headerCalculationTimeout) {
      clearTimeout(this.headerCalculationTimeout)
    }
    
    // Clear session token and timeout to prevent memory leaks
    this.clearSessionToken()
  },
  methods: {
    getPageTitle() {
      const parts = []

      // Base title
      let baseTitle = 'Upcoming Events'

      // Check for species filters
      if (this.speciesFilter && this.speciesFilter.length > 0) {
        const speciesLabels = this.speciesFilter.map(species => {
          // Find the corresponding label for the species value
          const speciesObj = this.speciesList.find(s => s.value === species.value || s.value === species)
          return speciesObj ? speciesObj.label : species.label || species
        })

        if (speciesLabels.length === 1) {
          baseTitle = `Upcoming ${speciesLabels[0]} Events`
        } else if (speciesLabels.length === 2) {
          baseTitle = `Upcoming ${speciesLabels[0]} & ${speciesLabels[1]} Events`
        } else if (speciesLabels.length <= 4) {
          baseTitle = `Upcoming ${speciesLabels.join(', ')} Events`
        } else {
          baseTitle = `Upcoming Events for ${speciesLabels.length} Species`
        }
      }

      // Check for location filter
      if (this.locationFilter) {
        const location = this.locationFilter.length > 25 ?
          `${this.locationFilter.substring(0, 25)}...` :
          this.locationFilter
        parts.push(`near ${location}`)
      }

      // Check for date filters
      if (this.startDateFilter || this.endDateFilter) {
        if (this.startDateFilter && this.endDateFilter) {
          const startDate = parse(this.startDateFilter).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          const endDate = parse(this.endDateFilter).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          parts.push(`${startDate} - ${endDate}`)
        } else if (this.startDateFilter) {
          const startDate = parse(this.startDateFilter).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          parts.push(`from ${startDate}`)
        } else if (this.endDateFilter) {
          const endDate = parse(this.endDateFilter).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
          parts.push(`through ${endDate}`)
        }
      }

      // Combine all parts
      if (parts.length === 0) {
        return baseTitle
      } else if (parts.length === 1) {
        return `${baseTitle} ${parts[0]}`
      } else {
        // For multiple filters, create a more natural sentence
        const lastPart = parts.pop()
        return `${baseTitle} ${parts.join(', ')} ${lastPart}`
      }
    },
    async initializeGooglePlaces() {
      await this.$nextTick()

      const input = this.$refs.locationInput
      if (!input) {
        console.error('Location input element not found')
        return
      }



      if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
        console.error('Google Maps API not loaded')
        return
      }


      const autocomplete = new google.maps.places.Autocomplete(input, {
        types: ['geocode'],
        fields: ['geometry', 'name', 'formatted_address', 'address_components']
      })


      autocomplete.addListener('place_changed', () => {
        console.log('Place changed event triggered')

        const place = autocomplete.getPlace()
        console.log('Selected place:', place)

        if (place.geometry && place.geometry.location) {
          console.log('Place has valid geometry')
          this.selectedLocation = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
            address: place.formatted_address || place.name,
            place: place
          }
          console.log('Selected location set:', this.selectedLocation)

          this.locationFilter = place.formatted_address || place.name
          console.log('Location filter updated:', this.locationFilter)

          this.applyFilters()
        } else {
          console.error('No valid geometry found for place')
        }
      })

      console.log('Google Places autocomplete setup complete')
    },
    onLocationInput() {
      this.showLocationDropdown = true
      
      // Create new session token if needed
      this.ensureSessionToken()

      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }

      // Debounce search to avoid too many API calls
      this.searchTimeout = setTimeout(() => {
        this.searchLocations()
      }, 300)
    },
    async searchLocations() {
      const query = this.locationSearchQuery

      if (!query || query.length < 2) {
        this.locationSearchResults = []
        return
      }

      // First, search states locally to avoid API costs
      const stateResults = this.searchStatesLocally(query)
      
      // If we found state matches, show them immediately
      if (stateResults.length > 0) {
        this.locationSearchResults = stateResults
        console.log(`Found ${stateResults.length} local state matches, skipping Places API`)
        return
      }

      // No state matches found, proceed with Places API for cities/addresses
      console.log('No local state matches, proceeding with Places API')

      // Check if Google Maps API is available, load it if not
      if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
        try {
          await this.loadGoogleMapsAPI()
        } catch (error) {
          console.error('Failed to load Google Maps API:', error)
          this.locationSearchResults = []
          return
        }
      }

      try {
        // Use Google Places API to search for locations
        const service = new google.maps.places.AutocompleteService()

        service.getPlacePredictions({
          input: query,
          types: ['geocode'],
          componentRestrictions: { country: 'US' },
          sessionToken: this.currentSessionToken
        }, (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            const placesResults = predictions.map(prediction => {
              const types = prediction.types || []
              const isState = types.includes('administrative_area_level_1')
              const isCity = types.includes('locality') || types.includes('sublocality')
              const isPostalCode = types.includes('postal_code')

              return {
                id: prediction.place_id,
                name: prediction.structured_formatting.main_text,
                description: prediction.structured_formatting.secondary_text,
                type: isState ? 'state' : (isCity ? 'city' : (isPostalCode ? 'postal_code' : 'location')),
                bgClass: isState ? 'bg-green-100' : 'bg-blue-100',
                icon: isState ? 'StateIcon' : (isCity ? 'CityIcon' : 'LocationIcon'),
                iconClass: isState ? 'text-green-600' : 'text-blue-600',
                prediction: prediction,
                isLocalResult: false
              }
            })

            // Filter out any states that we could have caught locally
            const filteredResults = placesResults.filter(result => {
              if (result.type === 'state') {
                console.log(`Filtering out state result: ${result.name} (should have been caught locally)`)
                return false
              }
              return true
            })

            this.locationSearchResults = filteredResults
          } else {
            console.error('Google Places API error:', status)
            this.locationSearchResults = []
          }
        })
      } catch (error) {
        console.error('Error searching locations:', error)
        this.locationSearchResults = []
      }
    },
    selectSuggestedLocation(destination) {
      this.selectedLocation = {
        name: destination.name,
        description: destination.description,
        coordinates: destination.region_info?.coordinates || {lat: 39.8283, lng: -98.5795}, // Default US center
        type: destination.type,
        place_id: null,
        region_info: destination.region_info // Include region_info for region filtering
      }

      this.locationFilter = destination.name
      this.showLocationDropdown = false
      this.allowBodyScroll()

      // Apply filters to update the store and trigger map update
      this.applyFilters()

      // Geocode the location to get accurate coordinates if not available
      if (!destination.region_info?.coordinates) {
        this.geocodeLocation(destination.name)
      }
    },
    async selectSearchResult(result) {
      // Handle local state results without API call
      if (result.isLocalResult && result.type === 'state') {
        console.log('Selected local state result, skipping Places API')
        
        const stateData = result.state_data
        this.selectedLocation = {
          name: stateData.name,
          description: `${stateData.name}, United States`,
          coordinates: stateData.coords,
          type: 'state',
          place_id: null, // No place_id for local results
          state_info: {
            name: stateData.name,
            abbreviation: stateData.abbr,
            coordinates: stateData.coords
          }
        }

        this.locationFilter = stateData.name
        this.showLocationDropdown = false
        this.allowBodyScroll()

        // No session token to clear since we didn't use Places API
        // Apply filters to update the store and trigger map update
        this.applyFilters()
        return
      }

      // Handle Places API results (cities, addresses, etc.)
      if (!result.id) {
        console.error('No place_id available for result:', result)
        return
      }

      // Ensure Google Maps API is loaded
      if (typeof google === 'undefined' || !google.maps || !google.maps.places) {
        try {
          await this.loadGoogleMapsAPI()
        } catch (error) {
          console.error('Failed to load Google Maps API:', error)
          return
        }
      }

      console.log('Selected Places API result, making details request')
      const service = new google.maps.places.PlacesService(document.createElement('div'))

      service.getDetails({
        placeId: result.id,
        fields: ['name', 'geometry', 'formatted_address', 'types', 'address_components'],
        sessionToken: this.currentSessionToken
      }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) {
          const locationType = this.getLocationType(place.types)

          // Extract state abbreviation if this is a state
          let stateInfo = null
          if (locationType === 'state' && place.address_components) {
            const stateComponent = place.address_components.find(component =>
              component.types.includes('administrative_area_level_1')
            )
            if (stateComponent) {
              stateInfo = {
                name: stateComponent.long_name,
                abbreviation: stateComponent.short_name,
                coordinates: {
                  lat: place.geometry.location.lat(),
                  lng: place.geometry.location.lng()
                }
              }
            }
          }

          this.selectedLocation = {
            name: place.name,
            description: place.formatted_address,
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng()
            },
            type: locationType,
            place_id: result.id,
            state_info: stateInfo // Include state_info for state filtering
          }

          this.locationFilter = place.name
          this.showLocationDropdown = false
          this.allowBodyScroll()

          // Clear session token after successful place details request
          this.clearSessionToken()

          // Apply filters to update the store and trigger map update
          this.applyFilters()
        } else {
          console.error('Place details request failed:', status)
          // Clear session token even on failure to avoid stuck tokens
          this.clearSessionToken()
        }
      })
    },
    async handleNearMe() {
      // Prevent multiple simultaneous requests
      if (this.isLoadingNearMe) {
        return
      }

      // Check if geolocation is supported
      if (!navigator.geolocation) {
        alert('Geolocation is not supported by this browser.')
        return
      }

      // Set loading state
      this.isLoadingNearMe = true

      try {
        // Get current position with better options
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            {
              enableHighAccuracy: true,
              timeout: 10000, // 10 second timeout
              maximumAge: 300000 // 5 minutes cache
            }
          )
        })

        const lat = position.coords.latitude
        const lng = position.coords.longitude

        // Ensure Google Maps API is loaded
        if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
          await this.loadGoogleMapsAPI()
        }

        // Reverse geocode to get address
        const geocoder = new google.maps.Geocoder()
        const geocodeResult = await new Promise((resolve, reject) => {
          geocoder.geocode({location: {lat, lng}}, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0])
            } else {
              reject(new Error(`Geocoding failed: ${status}`))
            }
          })
        })

        // Set location with geocoded address
        this.selectedLocation = {
          name: 'Current Location',
          description: geocodeResult.formatted_address,
          coordinates: {lat, lng},
          type: 'current_location',
          place_id: null
        }

        this.locationFilter = 'Current Location'
        this.showLocationDropdown = false
        this.allowBodyScroll()
        this.applyFilters()
      } catch (error) {
        console.error('Error getting location:', error)

        // Handle specific geolocation errors
        if (error.code) {
          let errorMessage = 'Could not get your current location.'

          switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please allow location access in your browser settings and try again.'
            break
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information is unavailable. Please check your device\'s location services.'
            break
          case error.TIMEOUT:
            errorMessage = 'Location request timed out. Please try again.'
            break
          }

          alert(errorMessage)
        } else {
          // If we have coordinates but geocoding failed, use coordinates as fallback
          if (error.message && error.message.includes('Geocoding failed')) {
            try {
              const position = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, {
                  enableHighAccuracy: false,
                  timeout: 5000,
                  maximumAge: 600000 // 10 minutes cache for fallback
                })
              })

              const lat = position.coords.latitude
              const lng = position.coords.longitude

              this.selectedLocation = {
                name: 'Current Location',
                description: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
                coordinates: {lat, lng},
                type: 'current_location',
                place_id: null
              }

              this.locationFilter = 'Current Location'
              this.showLocationDropdown = false
              this.allowBodyScroll()
              this.applyFilters()
            } catch (fallbackError) {
              alert('Unable to get your location. Please try selecting a location manually.')
            }
          } else {
            alert('Unable to get your location. Please try again or select a location manually.')
          }
        }
      } finally {
        // Clear loading state
        this.isLoadingNearMe = false
      }
    },
    closeLocationDropdown() {
      this.showLocationDropdown = false
      this.allowBodyScroll()
      
      // Clear session token when closing dropdown without selection
      if (this.currentSessionToken) {
        this.clearSessionToken()
      }
    },
    getLocationType(types) {
      if (!types) return 'location'

      if (types.includes('administrative_area_level_1')) return 'state'
      if (types.includes('locality') || types.includes('sublocality')) return 'city'
      if (types.includes('postal_code')) return 'city'

      return 'location'
    },
    async geocodeLocation(address) {
      try {
        const geocoder = new google.maps.Geocoder()

        geocoder.geocode({ address: address }, (results, status) => {
          if (status === 'OK' && results[0] && results[0].geometry) {
            this.selectedLocation = {
              ...this.selectedLocation,
              lat: results[0].geometry.location.lat(),
              lng: results[0].geometry.location.lng(),
              place: results[0]
            }
          } else {
            console.error('Geocoding failed:', status)
          }
        })
      } catch (error) {
        console.error('Error geocoding location:', error)
      }
    },
    async geocodeLocationFromQuery (locationString) {
      // Check for special "near-me" parameter
      if (locationString === 'near-me') {
        console.log('Detected near-me parameter, triggering geolocation')
        // Trigger the "Near Me" functionality
        await this.handleNearMe()
        return
      }

      // First check if it's a state in our local database
      const normalizedLocation = locationString.toLowerCase().replace(/\s+/g, '')

      // Check for state name match
      let stateData = this.usStates[normalizedLocation]

      // Check for state abbreviation match
      if (!stateData) {
        const upperLocation = locationString.toUpperCase()
        const stateKey = this.stateAbbrMap[upperLocation]
        if (stateKey) {
          stateData = this.usStates[stateKey]
        }
      }

      // If we found a state match, use local data
      if (stateData) {
        console.log('Found local state match for query param:', stateData.name)
        this.selectedLocation = {
          name: stateData.name,
          description: `${stateData.name}, United States`,
          coordinates: stateData.coords,
          type: 'state',
          place_id: null,
          state_info: {
            name: stateData.name,
            abbreviation: stateData.abbr,
            coordinates: stateData.coords
          }
        }
        this.locationFilter = stateData.name

        // Update store
        this.$store.dispatch('setSearchParam', {
          field: 'location',
          value: this.selectedLocation
        })

        return
      }

      // Not a state, geocode it using Google Maps API
      console.log('Not a state, geocoding location:', locationString)

      // Ensure Google Maps API is loaded
      if (typeof google === 'undefined' || !google.maps || !google.maps.Geocoder) {
        try {
          await this.loadGoogleMapsAPI()
        } catch (error) {
          console.error('Failed to load Google Maps API:', error)
          return
        }
      }

      return new Promise((resolve) => {
        const geocoder = new google.maps.Geocoder()

        geocoder.geocode({
          address: locationString,
          componentRestrictions: {country: 'US'}
        }, (results, status) => {
          if (status === 'OK' && results[0] && results[0].geometry) {
            const place = results[0]
            const locationType = this.getLocationType(place.types)

            // Extract state info if available
            let stateInfo = null
            if (locationType === 'state' && place.address_components) {
              const stateComponent = place.address_components.find(component =>
                component.types.includes('administrative_area_level_1')
              )
              if (stateComponent) {
                stateInfo = {
                  name: stateComponent.long_name,
                  abbreviation: stateComponent.short_name,
                  coordinates: {
                    lat: place.geometry.location.lat(),
                    lng: place.geometry.location.lng()
                  }
                }
              }
            }

            this.selectedLocation = {
              name: place.name || locationString,
              description: place.formatted_address,
              coordinates: {
                lat: place.geometry.location.lat(),
                lng: place.geometry.location.lng()
              },
              type: locationType,
              place_id: place.place_id,
              state_info: stateInfo
            }

            this.locationFilter = place.name || locationString

            // Update store
            this.$store.dispatch('setSearchParam', {
              field: 'location',
              value: this.selectedLocation
            })

            resolve()
          } else {
            console.error('Geocoding failed for query param:', status, locationString)
            resolve()
          }
        })
      })
    },
    calculateHeaderHeights() {
      // Use multiple timing attempts to ensure accurate calculation during layout transitions
      const attemptCalculation = (attempt = 1) => {
        this.$nextTick(() => {
          // Calculate top navigation height
          const topBar = document.getElementById('top-bar')
          if (topBar) {
            const actualHeight = topBar.offsetHeight
            // Only update if there's a meaningful difference to avoid unnecessary re-renders
            if (Math.abs(this.topNavHeight - actualHeight) > 2) {
              this.topNavHeight = actualHeight
            }
          }

          // Calculate filter header height
          if (this.$refs.stickyHeader) {
            const totalHeight = this.topNavHeight + this.$refs.stickyHeader.offsetHeight
            if (Math.abs(this.headerHeight - totalHeight) > 2) {
              this.headerHeight = totalHeight
            }
          }
          
          // If this is the first attempt and we're potentially in a transition,
          // try again after a brief delay to ensure layout has stabilized
          if (attempt === 1) {
            setTimeout(() => attemptCalculation(2), 150)
          }
        })
      }
      
      attemptCalculation()
    },
    debouncedCalculateHeaderHeights() {
      // Clear any existing timeout to debounce rapid calls
      if (this.headerCalculationTimeout) {
        clearTimeout(this.headerCalculationTimeout)
      }
      
      // Set a new timeout for the calculation
      this.headerCalculationTimeout = setTimeout(() => {
        this.calculateHeaderHeights()
      }, 100)
    },
    updateSpeciesFilter() {
      // Extract just the values from the multiselect objects
      const speciesValues = this.speciesFilter.map(species => species.value)
      this.$store.dispatch('setSearchParam', {field: 'species', value: speciesValues})
    },
    updateLocationFilter() {
      this.$store.dispatch('setSearchParam', {
        field: 'location',
        value: this.selectedLocation
      })
    },
    clearLocation() {
      this.locationFilter = ''
      this.selectedLocation = null
      this.$store.dispatch('setSearchParam', {field: 'location', value: null})
      this.$store.dispatch('setSearchParam', {field: 'radius', value: null})
    },
    updateDateRangeFilter() {
      this.$store.dispatch('setSearchParam', {field: 'startDate', value: this.startDateFilter})
      this.$store.dispatch('setSearchParam', {field: 'endDate', value: this.endDateFilter})
    },
    clearDateRange () {
      this.startDateFilter = ''
      this.endDateFilter = ''
      this.tempStartDate = ''
      this.tempEndDate = ''
      this.$store.dispatch('setSearchParam', {field: 'startDate', value: null})
      this.$store.dispatch('setSearchParam', {field: 'endDate', value: null})
    },
    clearAllFilters() {
      this.speciesFilter = []
      this.locationFilter = ''
      this.selectedLocation = null
      this.startDateFilter = ''
      this.endDateFilter = ''
      this.tempStartDate = ''
      this.tempEndDate = ''
      this.applyFilters()
    },
    async initializeFiltersFromStore () {
      // Initialize filters from URL query params first, then fall back to Vuex store

      // Species Filter
      let speciesParam = this.getParameterByName('species')
      if (speciesParam) {
        // Parse comma-separated species values from URL
        const speciesValues = speciesParam.split(',').map(s => s.trim())
        this.speciesFilter = speciesValues.map(speciesValue => {
          return this.speciesList.find(species => species.value === speciesValue)
        }).filter(Boolean) // Remove any undefined values

        // Update store with URL params
        this.$store.dispatch('setSearchParam', {
          field: 'species',
          value: speciesValues
        })
      } else {
        // Fall back to store values
        let speciesParameter = this.$store.state.searchParams.species
        if (speciesParameter && Array.isArray(speciesParameter)) {
          this.speciesFilter = speciesParameter.map(speciesValue => {
            return this.speciesList.find(species => species.value === speciesValue)
          }).filter(Boolean)
        }
      }

      // Location Filter
      let locationParam = this.getParameterByName('location')
      if (locationParam) {
        // Need to geocode the location string
        await this.geocodeLocationFromQuery(locationParam)
      } else {
        // Fall back to store values
        let locationParameter = this.$store.state.searchParams.location
        if (locationParameter) {
          this.selectedLocation = locationParameter
          this.locationFilter = locationParameter.name
        }
      }

      // Radius Filter
      let radiusParam = this.getParameterByName('radius')
      if (radiusParam) {
        const radiusValue = parseInt(radiusParam, 10)
        if (!isNaN(radiusValue) && radiusValue > 0) {
          this.selectedRadius = radiusValue
          this.$store.dispatch('setSearchParam', {
            field: 'radius',
            value: radiusValue
          })
        }
      } else {
        // Fall back to store values
        let radiusParameter = this.$store.state.searchParams.radius
        if (radiusParameter) {
          this.selectedRadius = radiusParameter
        }
      }

      // Start Date Filter
      let startDateParam = this.getParameterByName('startDate')
      if (startDateParam) {
        this.startDateFilter = startDateParam
        this.tempStartDate = startDateParam
        this.$store.dispatch('setSearchParam', {
          field: 'startDate',
          value: startDateParam
        })
      } else {
        // Fall back to store values
        let startDateFromStore = this.$store.state.searchParams.startDate
        if (startDateFromStore) {
          this.startDateFilter = startDateFromStore
          this.tempStartDate = startDateFromStore
        }
      }

      // End Date Filter
      let endDateParam = this.getParameterByName('endDate')
      if (endDateParam) {
        this.endDateFilter = endDateParam
        this.tempEndDate = endDateParam
        this.$store.dispatch('setSearchParam', {
          field: 'endDate',
          value: endDateParam
        })
      } else {
        // Fall back to store values
        let endDateFromStore = this.$store.state.searchParams.endDate
        if (endDateFromStore) {
          this.endDateFilter = endDateFromStore
          this.tempEndDate = endDateFromStore
        }
      }
    },
    getParameterByName(name, url) {
      if (!url) url = window.location.href
      name = name.replace(/[[\]]/g, '\\$&')
      var regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)')
      var results = regex.exec(url)
      if (!results) return null
      if (!results[2]) return ''
      return decodeURIComponent(results[2].replace(/\+/g, ' '))
    },
    handleResize() {
      const isMobile = window.innerWidth < 768
      this.$store.dispatch('setShowMap', !isMobile)
      
      // Recalculate header heights when switching between mobile/desktop
      // Use debounced method to avoid excessive calculations
      this.debouncedCalculateHeaderHeights()
    },
    handleLocationInput() {
      // Handle location input changes
    },
    selectNearMe() {
      // Handle "Near Me" option
    },
    selectSuggestion(suggestion) {
      // Handle selecting a suggested destination
    },
    getLocationTypeStyle(type) {
      // Implement logic to return appropriate styles based on location type
      return {
        bgClass: '',
        icon: '',
        iconClass: ''
      }
    },
    applyFilters() {
      // Always update species filter in store (even if empty)
      const speciesValues = this.speciesFilter && Array.isArray(this.speciesFilter)
        ? this.speciesFilter.map(species => species.value)
        : []

      this.$store.dispatch('setSearchParam', {
        field: 'species',
        value: speciesValues
      })

      // Update location filter in store (can be null to clear)
      this.$store.dispatch('setSearchParam', {
        field: 'location',
        value: this.selectedLocation
      })

      // Only apply radius filtering for non-state/non-region locations
      if (this.selectedLocation) {
        if (this.selectedLocation.type === 'state' || this.selectedLocation.type === 'region') {
          // For states and regions, don't apply radius filtering - just center the map
          this.$store.dispatch('setSearchParam', {
            field: 'radius',
            value: null
          })
          } else {
          // For cities and other locations, apply radius filtering
          this.$store.dispatch('setSearchParam', {
            field: 'radius',
            value: this.selectedRadius || 50 // Default to 50 miles
          })
        }
      } else {
        // Clear radius when no location is selected
        this.$store.dispatch('setSearchParam', {
          field: 'radius',
          value: null
        })
      }

      // Update date filters in store
      this.$store.dispatch('setSearchParam', {
        field: 'startDate',
        value: this.startDateFilter || null
      })

      this.$store.dispatch('setSearchParam', {
        field: 'endDate',
        value: this.endDateFilter || null
      })
    },
    scrollToTopOfShowsList() {
      // Scroll to the very top of the page
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })
    },
    toggleSpeciesDropdown() {
      this.showSpeciesDropdown = !this.showSpeciesDropdown
      this.showLocationDropdown = false
      this.showDateDropdown = false
      this.showRadiusDropdown = false // Close radius dropdown when species dropdown is toggled

      // Prevent scroll on mobile when dropdown is open
      if (window.innerWidth < 768) {
        if (this.showSpeciesDropdown) {
          this.preventBodyScroll()
        } else {
          this.allowBodyScroll()
        }
      }
    },
    clearSpecies() {
      this.speciesFilter = []
      this.showSpeciesDropdown = false
      this.allowBodyScroll()
      this.applyFilters()
    },
    applySpeciesFilter() {
      this.showSpeciesDropdown = false
      this.allowBodyScroll()
      this.applyFilters()
    },
    toggleLocationDropdown() {
      this.showLocationDropdown = !this.showLocationDropdown
      this.showSpeciesDropdown = false
      this.showDateDropdown = false
      this.showRadiusDropdown = false // Close radius dropdown when location dropdown is toggled

      // Prevent scroll on mobile when dropdown is open
      if (window.innerWidth < 768) {
        if (this.showLocationDropdown) {
          this.preventBodyScroll()
        } else {
          this.allowBodyScroll()
        }
      }
    },
    onLocationSearch() {
      this.showLocationDropdown = true
      this.searchLocations()

      // Prevent scroll on mobile when dropdown is open
      if (window.innerWidth < 768) {
        this.preventBodyScroll()
      }
    },
    toggleRadiusDropdown() {
      this.showRadiusDropdown = !this.showRadiusDropdown
      this.showLocationDropdown = false
      this.showSpeciesDropdown = false
      this.showDateDropdown = false

      // Prevent scroll on mobile when dropdown is open
      if (window.innerWidth < 768) {
        if (this.showRadiusDropdown) {
          this.preventBodyScroll()
        } else {
          this.allowBodyScroll()
        }
      }
    },
    getRadiusLabel() {
      const radiusOption = this.radiusOptions.find(option => option.value === this.selectedRadius)
      return radiusOption ? radiusOption.label : `${this.selectedRadius} miles`
    },
    toggleDateDropdown() {
      this.showDateDropdown = !this.showDateDropdown
      this.showSpeciesDropdown = false
      this.showLocationDropdown = false
      this.showRadiusDropdown = false // Close radius dropdown when date dropdown is toggled

      // Set default values when opening dropdown (only if temp values are empty)
      if (this.showDateDropdown && !this.tempStartDate && !this.tempEndDate) {
        const today = new Date()
        const oneWeekLater = new Date(today)
        oneWeekLater.setDate(today.getDate() + 7)
        
        this.tempStartDate = today.toISOString().slice(0, 10) // YYYY-MM-DD format
        this.tempEndDate = oneWeekLater.toISOString().slice(0, 10) // YYYY-MM-DD format
      }

      // Prevent scroll on mobile when dropdown is open
      if (window.innerWidth < 768) {
        if (this.showDateDropdown) {
          this.preventBodyScroll()
        } else {
          this.allowBodyScroll()
        }
      }
    },
    getDateRangeLabel() {
      if (this.startDateFilter && this.endDateFilter) {
        return `${this.startDateFilter} - ${this.endDateFilter}`
      } else if (this.startDateFilter) {
        return `From ${this.startDateFilter}`
      } else if (this.endDateFilter) {
        return `To ${this.endDateFilter}`
      } else {
        return 'Date Range'
      }
    },
    applyDateFilter() {
      this.startDateFilter = this.tempStartDate
      this.endDateFilter = this.tempEndDate
      this.showDateDropdown = false
      this.allowBodyScroll()
      this.applyFilters()
    },
    clearDateFilter() {
      this.startDateFilter = ''
      this.endDateFilter = ''
      this.tempStartDate = ''
      this.tempEndDate = ''
      this.showDateDropdown = false
      this.allowBodyScroll()
      this.applyFilters()
    },
    toggleAllFilters() {
      this.showSpeciesDropdown = !this.showSpeciesDropdown
      this.showLocationDropdown = !this.showLocationDropdown
      this.showDateDropdown = !this.showDateDropdown
    },
    closeAllDropdowns() {
      this.showSpeciesDropdown = false
      this.showLocationDropdown = false
      this.showDateDropdown = false
      this.showRadiusDropdown = false
      this.allowBodyScroll()
      
      // Clear session token when closing all dropdowns
      if (this.currentSessionToken) {
        this.clearSessionToken()
      }
    },
    preventBodyScroll() {
      document.body.style.overflow = 'hidden'
      document.body.style.position = 'fixed'
      document.body.style.width = '100%'
    },
    allowBodyScroll() {
      document.body.style.overflow = ''
      document.body.style.position = ''
      document.body.style.width = ''
    },
    // Session token management for Google Places API cost optimization
    ensureSessionToken() {
      // Create a new session token if one doesn't exist
      if (!this.currentSessionToken) {
        this.createSessionToken()
      }
      
      // Reset the session token timeout (auto-expire after 3 minutes of inactivity)
      this.resetSessionTokenTimeout()
    },
    createSessionToken() {
      if (typeof google !== 'undefined' && google.maps && google.maps.places) {
        this.currentSessionToken = new google.maps.places.AutocompleteSessionToken()
        console.log('Created new Google Places session token')
      }
    },
    clearSessionToken() {
      this.currentSessionToken = null
      if (this.sessionTokenTimeout) {
        clearTimeout(this.sessionTokenTimeout)
        this.sessionTokenTimeout = null
      }
      console.log('Cleared Google Places session token')
    },
    resetSessionTokenTimeout() {
      // Clear existing timeout
      if (this.sessionTokenTimeout) {
        clearTimeout(this.sessionTokenTimeout)
      }
      
      // Set new timeout to clear session token after 3 minutes of inactivity
      this.sessionTokenTimeout = setTimeout(() => {
        this.clearSessionToken()
        console.log('Session token expired due to inactivity')
      }, 180000) // 3 minutes
    },
    // State search optimization - avoid Places API for state searches
    searchStatesLocally(query) {
      if (!query || query.length < 2) return []

      const searchTerm = query.toLowerCase().replace(/\s+/g, '')
      const results = []

      // Check for exact abbreviation match first
      const upperQuery = query.toUpperCase()
      if (this.stateAbbrMap[upperQuery]) {
        const stateKey = this.stateAbbrMap[upperQuery]
        const state = this.usStates[stateKey]
        results.push({
          id: `state_${stateKey}`,
          name: state.name,
          description: `${state.name}, United States`,
          type: 'state',
          bgClass: 'bg-green-100',
          icon: 'StateIcon',
          iconClass: 'text-green-600',
          state_data: state,
          isLocalResult: true
        })
      }

      // Search by state name (fuzzy matching)
      Object.keys(this.usStates).forEach(stateKey => {
        const state = this.usStates[stateKey]
        const stateName = state.name.toLowerCase().replace(/\s+/g, '')
        
        // Skip if already added by abbreviation
        if (results.some(r => r.id === `state_${stateKey}`)) return

        // Check for partial matches
        if (stateName.includes(searchTerm) || searchTerm.includes(stateName.substring(0, 4))) {
          results.push({
            id: `state_${stateKey}`,
            name: state.name,
            description: `${state.name}, United States`,
            type: 'state',
            bgClass: 'bg-green-100',
            icon: 'StateIcon',
            iconClass: 'text-green-600',
            state_data: state,
            isLocalResult: true
          })
        }
      })

      return results.slice(0, 5) // Limit to 5 state results
    },
    loadGoogleMapsAPI() {
      return new Promise((resolve, reject) => {
        // Check if already loaded
        if (typeof google !== 'undefined' && google.maps && google.maps.places) {
          resolve()
          return
        }

        // Check if script is already being loaded
        if (document.querySelector('script[src*="maps.googleapis.com"]')) {
          // Wait for it to load
          const checkLoaded = () => {
            if (typeof google !== 'undefined' && google.maps && google.maps.places) {
              resolve()
            } else {
              setTimeout(checkLoaded, 100)
            }
          }
          checkLoaded()
          return
        }

        // Load the script
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&libraries=places`
        script.async = true
        script.defer = true

        script.onload = () => {
          if (typeof google !== 'undefined' && google.maps && google.maps.places) {
            resolve()
          } else {
            reject(new Error('Google Maps API failed to initialize properly'))
          }
        }

        script.onerror = () => {
          reject(new Error('Google Maps API failed to load'))
        }

        document.head.appendChild(script)
      })
    },
    handleClearLocationFromMap() {
      // Clear location filter when triggered from map boundary control
      this.clearLocation()
    },
    handleMapBoundsChanged(bounds) {
      // Update map bounds in store first
      this.$store.dispatch('setMapBounds', bounds)

      // Trigger any additional filtering logic if needed
      // The store getters will automatically recalculate based on new bounds
    },
    handleLocationInputFocus() {
      // Prevent keyboard from closing on Safari mobile
      if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i)) {
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.overflow = 'hidden';
      }
    },
    handleLocationInputBlur() {
      // Re-allow scrolling and position after blur
      if (navigator.userAgent.match(/iPhone/i) || navigator.userAgent.match(/iPad/i) || navigator.userAgent.match(/iPod/i)) {
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
      }
    }
  },
  watch: {
    '$store.state.mapBounds': {
      handler(newBounds, oldBounds) {
        // Only scroll to top if:
        // 1. Map is visible (showMap is true)
        // 2. Map bounds have actually changed
        // 3. This isn't the initial load (oldBounds exists)
        if (this.showMap && oldBounds && newBounds && JSON.stringify(newBounds) !== JSON.stringify(oldBounds)) {
          // Show skeleton loading
          if (!this.$store.state.searchParams.radius) {
            this.isLoadingMapBounds = true
          }

          this.$nextTick(() => {
            this.scrollToTopOfShowsList()

            // Hide skeleton loading after a brief delay
            setTimeout(() => {
              this.isLoadingMapBounds = false
            }, 800) // 800ms loading simulation
          })
        }
      },
      deep: true
    },
    selectedRadius: {
      handler(newRadius, oldRadius) {
        // Apply filters when radius changes (but only if location is selected and it's not a state)
        if (this.selectedLocation && this.selectedLocation.type !== 'state' && newRadius !== oldRadius) {
          this.applyFilters()
        }
      }
    },
    speciesFilter: {
      handler(newSpecies, oldSpecies) {
        // Scroll to top when species filter changes (but not on initial load)
        if (oldSpecies !== undefined && JSON.stringify(newSpecies) !== JSON.stringify(oldSpecies)) {
          this.$nextTick(() => {
            this.scrollToTopOfShowsList()
          })
        }
      },
      deep: true
    },
    startDateFilter: {
      handler(newDate, oldDate) {
        // Scroll to top when start date filter changes (but not on initial load)
        if (oldDate !== undefined && newDate !== oldDate) {
          this.$nextTick(() => {
            this.scrollToTopOfShowsList()
          })
        }
      }
    },
    endDateFilter: {
      handler(newDate, oldDate) {
        // Scroll to top when end date filter changes (but not on initial load)
        if (oldDate !== undefined && newDate !== oldDate) {
          this.$nextTick(() => {
            this.scrollToTopOfShowsList()
          })
        }
      }
    }
  }
}
</script>

<style scoped>
/* Mobile Responsive */
@media (max-width: 1023px) {
  .main-layout {
    flex-direction: column;
    height: auto;
    min-height: 100vh;
  }

  .home-container {
    height: auto;
    min-height: 100vh;
    overflow: visible;
  }

  .sidebar-container {
    width: 100%;
    height: auto;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }

  .content-container {
    height: auto;
    min-height: calc(100vh - 12.5rem);
  }

  .content-header {
    position: relative;
  }

  .hybrid-container {
    flex-direction: column;
    height: auto;
    min-height: 800px;
  }

  .map-container,
  .hybrid-map {
    height: 24rem;
  }

  .grid-container {
    grid-template-columns: 1fr;
  }
}

/* Small Mobile */
@media (max-width: 640px) {
  .main-layout {
    gap: 0;
  }

  .scrollable-content {
    padding: 1rem;
  }

  .content-header {
    padding: 1rem;
  }

  .sidebar-content {
    padding: 0.75rem;
  }
}
</style>

<style>
/* Global styles for Google Places Autocomplete - not scoped */
.pac-container {
  z-index: 9999 !important;
  border-radius: 8px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
  font-family: inherit !important;
}

.pac-item {
  padding: 8px 12px !important;
  font-size: 14px !important;
  border-bottom: 1px solid #f3f4f6 !important;
  cursor: pointer !important;
}

.pac-item:hover {
  background-color: #f9fafb !important;
}

.pac-item-selected {
  background-color: #eff6ff !important;
}

.pac-matched {
  font-weight: 600 !important;
}

.pac-item-query {
  color: #374151 !important;
}

/* Ensure the input has proper styling */
.location-input {
  position: relative !important;
  z-index: 1 !important;
}

/* Fix potential conflicts with Tailwind/other CSS */
.pac-container::after {
  display: none !important;
}

.pac-logo::after {
  display: none !important;
}
</style>

